import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host { display: contents; }

    /* ══════════════════════════════════════════════════════════════════════
       Keyframes
    ══════════════════════════════════════════════════════════════════════ */
    @keyframes uwc-dlg-in {
      from { opacity: 0; transform: scale(0.96) translateY(-10px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);      }
    }
    @keyframes uwc-dlg-out {
      from { opacity: 1; transform: scale(1)    translateY(0);      }
      to   { opacity: 0; transform: scale(0.96) translateY(-10px);  }
    }
    @keyframes uwc-dlg-bd-in  { from { opacity: 0; } to { opacity: 1; } }
    @keyframes uwc-dlg-bd-out { from { opacity: 1; } to { opacity: 0; } }

    /* For bottom-positioned dialogs */
    @keyframes uwc-dlg-in-up {
      from { opacity: 0; transform: scale(0.96) translateY(10px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);    }
    }

    /* ══════════════════════════════════════════════════════════════════════
       Native <dialog> resets + base layout
    ══════════════════════════════════════════════════════════════════════ */
    dialog.uwc-dlg {
      /* Reset browser UA styles */
      border:     none;
      padding:    0;
      background: none;
      color:      inherit;
      outline:    none;
      overflow:   visible; /* handled by content area */

      /* Fixed positioning (top layer also uses fixed) */
      position: fixed;
      inset:    0;
      margin:   auto;

      /* Dimensions — set via inline styles in _applyPosition() */
      width:      50vw;
      max-width:  calc(100vw - 2rem);
      max-height: calc(100dvh - 2rem);
      height:     fit-content;

      /* Visual */
      background:    var(--uwc-dialog-bg,     ${t.surface});
      border:        var(--uwc-dialog-border,  1px solid ${t.border});
      border-radius: var(--uwc-dialog-radius,  ${t.radiusXl});
      box-shadow:    var(--uwc-dialog-shadow,  ${t.shadowLg});

      /* closed state: keep browser default display:none intact */
      overflow: hidden;
    }

    /* Only apply layout + animations when the dialog is actually open */
    dialog[open].uwc-dlg {
      display:        flex;
      flex-direction: column;
      overflow:       hidden;
    }

    /* Entry animation (open, not mid-close) */
    dialog[open].uwc-dlg:not(.is-closing) {
      animation: uwc-dlg-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    /* Close animation — dialog still has [open] while animating out */
    dialog[open].uwc-dlg.is-closing {
      animation: uwc-dlg-out 180ms ease both;
    }

    /* ── Backdrop ────────────────────────────────────────────────────────── */
    dialog[open].uwc-dlg::backdrop {
      background: var(--uwc-dialog-backdrop, rgba(0, 0, 0, 0.45));
      animation: uwc-dlg-bd-in 220ms ease both;
    }

    dialog[open].uwc-dlg.is-closing::backdrop {
      animation: uwc-dlg-bd-out 180ms ease both;
    }

    /* No-modal: transparent backdrop */
    dialog.uwc-dlg.uwc-dlg--no-modal::backdrop {
      background: transparent;
    }

    /* ══════════════════════════════════════════════════════════════════════
       Positions — inset/margin handled via inline styles in _applyPosition()
       (inline styles beat the native <dialog> UA stylesheet; CSS classes don't)
       Only animation-name overrides live here.
    ══════════════════════════════════════════════════════════════════════ */

    .uwc-dlg--bottom,
    .uwc-dlg--bottom-left,
    .uwc-dlg--bottom-right {
      animation-name: uwc-dlg-in-up;
    }

    .uwc-dlg--bottom.is-closing,
    .uwc-dlg--bottom-left.is-closing,
    .uwc-dlg--bottom-right.is-closing {
      animation-name: uwc-dlg-out;
    }

    /* Maximized — visual overrides only; inset/size set via inline styles */
    .uwc-dlg--maximized {
      max-width:     100vw;
      max-height:    100dvh;
      border-radius: 0;
    }

    /* ══════════════════════════════════════════════════════════════════════
       Header
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-dlg__header {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      gap:             ${t.space2};
      padding:         0.875rem ${t.space4} 0.875rem ${t.space5};
      flex-shrink:     0;
      border-bottom:   1px solid var(--uwc-dialog-header-border, ${t.borderSubtle});
      background:      var(--uwc-dialog-header-bg, transparent);
      user-select:     none;
    }

    /* Show grab cursor when draggable and not using a button */
    .uwc-dlg--draggable .uwc-dlg__header { cursor: grab; }
    .uwc-dlg--dragging  .uwc-dlg__header { cursor: grabbing; }

    .uwc-dlg__title {
      font-size:   var(--uwc-dialog-title-size, ${t.fontSizeLg});
      font-weight: var(--uwc-dialog-title-weight, ${t.fontWeightSemibold});
      color:       var(--uwc-dialog-title-color, ${t.text});
      line-height: 1.25;
      flex:        1;
      min-width:   0;
      white-space: nowrap;
      overflow:    hidden;
      text-overflow: ellipsis;
    }

    .uwc-dlg__actions {
      display:     flex;
      align-items: center;
      gap:         0.125rem;
      flex-shrink: 0;
    }

    /* ══════════════════════════════════════════════════════════════════════
       Content / body
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-dlg__content {
      flex:       1;
      overflow-y: auto;
      overflow-x: hidden;
      padding:    var(--uwc-dialog-content-padding, ${t.space5});
      color:      var(--uwc-dialog-content-color, ${t.text});
      line-height: 1.6;

      /* Custom scrollbar */
      scrollbar-width: thin;
      scrollbar-color: ${t.border} transparent;
    }
    .uwc-dlg__content::-webkit-scrollbar       { width: 5px; }
    .uwc-dlg__content::-webkit-scrollbar-thumb {
      background: ${t.border};
      border-radius: ${t.radiusFull};
    }

    /* ══════════════════════════════════════════════════════════════════════
       Footer
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-dlg__footer {
      display:       flex;
      align-items:   center;
      justify-content: flex-end;
      gap:           ${t.space2};
      padding:       0.875rem ${t.space5};
      flex-shrink:   0;
      border-top:    1px solid var(--uwc-dialog-footer-border, ${t.borderSubtle});
      background:    var(--uwc-dialog-footer-bg, transparent);
    }

    .uwc-dlg__footer--hide { display: none; }

    /* ══════════════════════════════════════════════════════════════════════
       Resize handle
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-dlg__resize {
      position:  absolute;
      right:     0;
      bottom:    0;
      width:     1rem;
      height:    1rem;
      cursor:    se-resize;
      /* Visual indicator: two diagonal notch lines */
      background-image:
        linear-gradient(
          135deg,
          transparent 30%,
          ${t.border} 30%, ${t.border} 45%,
          transparent 45%, transparent 55%,
          ${t.border} 55%, ${t.border} 70%,
          transparent 70%
        );
    }

    /* ══════════════════════════════════════════════════════════════════════
       Reduced motion
    ══════════════════════════════════════════════════════════════════════ */
    @media (prefers-reduced-motion: reduce) {
      dialog.uwc-dlg,
      dialog.uwc-dlg.is-closing,
      dialog.uwc-dlg::backdrop,
      dialog.uwc-dlg.is-closing::backdrop {
        animation: none;
      }
    }

    /* ══════════════════════════════════════════════════════════════════════
       Mobile: full-width on small screens
    ══════════════════════════════════════════════════════════════════════ */
    @media (max-width: 480px) {
      dialog.uwc-dlg:not(.uwc-dlg--maximized) {
        width:      calc(100vw - 1.5rem) !important;
        max-height: calc(100dvh - 4rem)  !important;
        inset:      auto 0 0 0 !important;
        margin:     0 auto !important;
        border-bottom-left-radius:  0;
        border-bottom-right-radius: 0;
        animation-name: uwc-dlg-in-up;
      }
    }
  `,
];
